import { type Dispatch, type MouseEvent, type SetStateAction } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { journalsQueries } from "@/entities/journal";
import { useCoordinator } from "@/shared/lib/hooks";
import { useAppRoutes } from "@shared/lib/react-router";
import { type ModalState } from "../types";

type UseActionType = {
  handleDelete: any;
  modalState: ModalState;
  setModalState: Dispatch<SetStateAction<ModalState>>;
  handleSendToSign: any;
  handleReject: any;
  handleReturn: any;
  handleSendToSignBatch: any;
  setSelectedRowKeys: any;
  setIsColumnSelectorVisible: any;
  handleStatusReturn: any;
  handleSendToSignTwo: any;
  handleRejectTwo: any;
  handleReturnTwo: any;
  handleStatusReturnTwo: any;
};

export const useAction = ({
  handleDelete,
  modalState,
  setModalState,
  handleSendToSign,
  handleReject,
  handleReturn,
  handleSendToSignBatch,
  setSelectedRowKeys,
  setIsColumnSelectorVisible,
  handleStatusReturn,
  handleSendToSignTwo,
  handleRejectTwo,
  handleReturnTwo,
  handleStatusReturnTwo,
}: UseActionType) => {
  const navigate = useNavigate();
  const { projectId, objectId, mainJournalId, titleId } = useCoordinator();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const appRoutes = useAppRoutes();

  const itemNoVersionId = searchParams.get("itemNoVersionId");
  const itemPartOne = searchParams.get("itemPartOne");
  const itemPartTwo = searchParams.get("itemPartTwo");
  // Определяем часть по наличию параметра itemPartTwo в URL
  const isPartTwoContext = Boolean(itemPartTwo);
  const handleAction = async (action: string) => {
    let isDelete = false;
    switch (action) {
      case "SEND":
        if (modalState.sectionTwoAction) {
          await handleSendToSignTwo({
            itemNoVersionId: itemNoVersionId || "",
            itemPartTwoId: itemPartTwo || "",
          });
        } else {
          await handleSendToSign({
            itemNoVersionId: itemNoVersionId || "",
            itemPartOneId: itemPartOne || "",
          });
        }
        break;
      case "REJECT":
        if (isPartTwoContext) {
          handleRejectTwo({
            itemNoVersionId: itemNoVersionId || "",
            comment: modalState.inputValue || "",
            signerEmployeeId: modalState.signerEmployeeId || "",
            itemPartTwoId: itemPartTwo || "",
          });
        } else {
          handleReject({
            itemNoVersionId: itemNoVersionId || "",
            comment: modalState.inputValue || "",
            signerEmployeeId: modalState.signerEmployeeId || "",
            itemPartOneId: itemPartOne || "",
          });
        }

        break;
      case "DELETE":
        handleDelete({
          itemNoVersionId: itemNoVersionId || "",
        });
        isDelete = true;
        break;
      case "RETURN":
        if (isPartTwoContext) {
          handleReturnTwo({
            itemNoVersionId: itemNoVersionId || "",
            itemPartTwoId: itemPartTwo || "",
          });
        } else {
          handleReturn({
            itemNoVersionId: itemNoVersionId || "",
            itemPartOneId: itemPartOne || "",
          });
        }
        break;
      case "SEND_MULTIPLE":
        await handleSendToSignBatch(modalState?.actionPayload);
        setSelectedRowKeys([]);
        setIsColumnSelectorVisible(false);
        break;
      case "RECALL":
        if (isPartTwoContext) {
          handleStatusReturnTwo({
            itemNoVersionId: itemNoVersionId || "",
            itemPartTwoId: itemPartTwo || "",
          });
        } else {
          handleStatusReturn({
            itemNoVersionId: itemNoVersionId || "",
            itemPartOneId: itemPartOne || "",
          });
        }
        break;
      case "VIEW":
        break;
      default:
        break;
    }
    setModalState({
      visible: false,
      title: "",
      info: "",
      confirmText: "",
      cancelText: "",
      inputPlaceholder: "",
      inputValue: "",
      isInputModal: false,
      action: null,
      signerEmployeeId: null,
      actionPayload: null,
      sectionTwoAction: null,
    });
    // Restore or clear route
    if (isDelete) {
      // Navigation is handled by URL params
      setSearchParams({});
    } else {
      const params = new URLSearchParams(searchParams);
      const id = params.get("itemNoVersionId");
      const currentAction = params.get("action");
      const origin = params.get("origin");
      const shouldReturnToView = Boolean(
        id && (currentAction === "VIEW" || currentAction === "EDIT" || origin === "view"),
      );

      if (shouldReturnToView && id) {
        params.set("action", "VIEW");
        params.delete("origin");
        if (itemPartOne) params.set("itemPartOne", itemPartOne); else params.delete("itemPartOne");
        if (itemPartTwo) params.set("itemPartTwo", itemPartTwo); else params.delete("itemPartTwo");
        setSearchParams(params);
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: journalsQueries.getSectionFourItem({ itemNoVersionId: id }).queryKey,
          });
        }, 0);
      } else if (id) {
        // Originated from table: keep table context, just ensure item query is fresh (if any)
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: journalsQueries.getSectionFourItem({ itemNoVersionId: id }).queryKey,
          });
        }, 0);
      }
    }
  };

  const handleCloseForm = (

    event?: MouseEvent<HTMLSpanElement>,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    event && event.stopPropagation();
    setModalState({
      visible: false,
      title: "",
      info: "",
      confirmText: "",
      cancelText: "",
      action: null,
      signerEmployeeId: null,
      sectionTwoAction: null,
      isDivider: false,
    });
    const params = new URLSearchParams(searchParams);
    const id = params.get("itemNoVersionId");
    const origin = params.get("origin");
    // If we came from VIEW (origin=view), return to VIEW; otherwise clear params
    if (id && origin === "view") {
      params.set("action", "VIEW");
      params.delete("origin");
      if (itemPartOne) params.set("itemPartOne", itemPartOne); else params.delete("itemPartOne");
      if (itemPartTwo) params.set("itemPartTwo", itemPartTwo); else params.delete("itemPartTwo");
      setSearchParams(params);
    } else {
      setSearchParams({});
    }
  };

  const handleOpenForm = ({ itemNoVersionId }: { formType: string | null; itemNoVersionId?: string }) => {
    // Navigate to form page
    const id = itemNoVersionId || searchParams.get("itemNoVersionId") || "new";
    const path = appRoutes.journals.section4Form(projectId, objectId, mainJournalId, titleId, id);
    navigate(path);
  };

  return { handleAction, handleCloseForm, handleOpenForm };
};
